import { ReactNode, useCallback } from "react";

import { AnalyticsCommonProps } from "hooks/useAnalytics";
import { useToggle } from "hooks/useToggle";

import { BoxProps } from "../Box";
import { TypographyProps } from "../Typography";
import CollapsibleSectionWrapper from "./components/Wrapper";
import CollapsibleSectionHeader from "./components/Header";
import CollapsibleSectionContent from "./components/Content";
import { CollapsibleSectionAriaLevel } from "./components/types";

type CollapsibleSectionProps = {
  children: ReactNode;
  title: ReactNode;
  titleTag?: TypographyProps["tag"];
  titleVariant?: TypographyProps["variant"];
  className?: string;
  headerSuffix?: ReactNode;
  titleSuffix?: ReactNode;
  contentGap?: BoxProps["gap"];
  ariaLevel: CollapsibleSectionAriaLevel;
  ariaLabel: string;
  fullbleed?: boolean;
  headerAlign?: BoxProps["align"];
} & Pick<BoxProps, "fullHeight" | "fullWidth" | "margin" | "padding"> &
  AnalyticsCommonProps &
  (
    | {
        onToggle?: never;
        isCollapsed?: never;
        initialIsCollapsed?: boolean;
      }
    | {
        onToggle: () => void;
        isCollapsed: boolean;
        initialIsCollapsed?: never;
      }
  );

const CollapsibleSection = ({
  headerAlign = "center",
  children,
  title,
  className,
  headerSuffix,
  titleSuffix,
  titleTag,
  titleVariant,
  ariaLevel,
  ariaLabel,
  isCollapsed,
  initialIsCollapsed = true,
  onToggle,
  contentGap,
  analyticsPage,
  analyticsTitle,
  analyticsProps,
  fullbleed,
  ...restBoxProps
}: CollapsibleSectionProps) => {
  const [isCollapsedInternal, onToggleInternal] = useToggle(initialIsCollapsed);

  const handleToggle = useCallback(() => {
    (onToggle || onToggleInternal)();
  }, [onToggleInternal, onToggle]);

  return (
    <CollapsibleSectionWrapper
      className={className}
      isCollapsed={onToggle ? isCollapsed : isCollapsedInternal}
      onToggle={handleToggle}
      {...restBoxProps}
    >
      {title && (
        <CollapsibleSectionHeader
          align={headerAlign}
          titleTag={titleTag}
          titleVariant={titleVariant}
          titleSuffix={titleSuffix}
          suffix={headerSuffix}
          ariaLevel={ariaLevel}
          ariaLabel={ariaLabel}
          analyticsPage={analyticsPage}
          analyticsTitle={analyticsTitle}
          analyticsProps={analyticsProps}
        >
          {title}
        </CollapsibleSectionHeader>
      )}
      <CollapsibleSectionContent fullbleed={fullbleed} gap={contentGap}>
        {children}
      </CollapsibleSectionContent>
    </CollapsibleSectionWrapper>
  );
};

CollapsibleSection.displayName = "DS.CollapsibleSection";

export default CollapsibleSection;
